import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The questions an agent asks, read-only (2026-10-04, design §16.7.2 AS AMENDED 2026-10-04).
 *
 * A survey agent asks the questionnaire it was built with — the document uploaded when it was built —
 * so a campaign has no questions of its own to type. New campaign shows them in the design's compact
 * "questions read" preview (number, text, kind tag); Campaign shows them as the design's question cards.
 * Both list a choice question's choices under it, and with two arms say which arm a statement is in.
 *
 * The text is the questionnaire's first language when no language is asked for (`questionTextOf`): the
 * agent asks each person in their own language, from the same questionnaire.
 */
import { Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import {
  voiceAgentQuestionArmText,
  voiceAgentQuestionChoiceText,
  voiceAgentQuestionKindLabel,
  voiceAgentQuestionKindTone,
  armCountOf,
} from "./campaign-copy.js";
import { NO_QUESTION_TEXT, questionTextOf } from "./survey-result-copy.js";
import type { VoiceAgentQuestionView } from "../../api/slice/campaign.js";

export interface VoiceAgentQuestionListProps {
  readonly question: readonly VoiceAgentQuestionView[];
  /**
   * `set`: New campaign's question-set card rows (design export 2026-10-09); `preview`: the compact rows
   * it used before; `card`: Campaign's question cards.
   */
  readonly look: "set" | "preview" | "card";
}

export function VoiceAgentQuestionList({ question, look }: VoiceAgentQuestionListProps): ReactNode {
  usePortalTranslation("operations");
  const armCount = armCountOf(question);
  const prefix = look === "card" ? "c-camp-question" : look === "set" ? "c-camp-set" : "c-camp-preview";
  return (
    <ol className={look === "card" ? "c-camp-questions" : look === "set" ? "c-camp-set-list" : "c-camp-preview"} aria-label={portalText("operations", "the_agent_s_questions_5eae3d30")}>
      {question.map((one, index) => {
        const arm = voiceAgentQuestionArmText(one, armCount);
        const choice = voiceAgentQuestionChoiceText(one, null);
        return (
          <li className={look === "card" ? prefix : `${prefix}-row`} key={one.questionCode}>
            <span className={`${prefix}-n`}>{index + 1}</span>
            <span className={`${prefix}-text`}>
              {questionTextOf(one.text, null) ?? NO_QUESTION_TEXT()}
              {choice === null ? null : <span className="c-camp-question-choice">{choice}</span>}
            </span>
            <span className={`${prefix}-meta`}>
              {arm === null ? null : <span className="c-camp-question-count">{arm}</span>}
              <Tag tone={voiceAgentQuestionKindTone(one.questionKind)}>{voiceAgentQuestionKindLabel(one)}</Tag>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
